// =============================================================================
// React Application Entry Point
// =============================================================================

import React from 'react';
import ReactDOM from 'react-dom/client';
import { HashRouter } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

// Import styles
import './index.css';

// Import main app component
import App from './App';

// Import contexts
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import { SessionProvider } from './contexts/SessionContext';

// Import error boundary
import ErrorBoundary from './components/ErrorBoundary';

// Performance monitoring (development only)
if (import.meta.env.DEV) {
  // Enable React DevTools profiler
  if (typeof window !== 'undefined' && window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
    const originalOnCommitFiberRoot = window.__REACT_DEVTOOLS_GLOBAL_HOOK__.onCommitFiberRoot;
    window.__REACT_DEVTOOLS_GLOBAL_HOOK__.onCommitFiberRoot = (id, root, priorityLevel) => {
      // Custom profiling logic could go here
      if (originalOnCommitFiberRoot) {
        originalOnCommitFiberRoot(id, root, priorityLevel);
      }
    };
  }
}

// Main application wrapper
function AppWrapper() {
  return (
    <ErrorBoundary>
      <HashRouter>
        <ThemeProvider>
          <AuthProvider>
            <WorkspaceProvider>
              <SessionProvider>
                <App />
                <Toaster
                  position="top-right"
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: 'hsl(var(--card))',
                      color: 'hsl(var(--card-foreground))',
                      border: '1px solid hsl(var(--border))',
                    },
                    success: {
                      iconTheme: {
                        primary: 'hsl(var(--primary))',
                        secondary: 'hsl(var(--primary-foreground))',
                      },
                    },
                    error: {
                      iconTheme: {
                        primary: 'hsl(var(--destructive))',
                        secondary: 'hsl(var(--destructive-foreground))',
                      },
                    },
                  }}
                />
              </SessionProvider>
            </WorkspaceProvider>
          </AuthProvider>
        </ThemeProvider>
      </HashRouter>
    </ErrorBoundary>
  );
}

// Initialize the application
function initializeApp() {
  const root = ReactDOM.createRoot(document.getElementById('root'));
  
  // Hide loading screen
  const loadingScreen = document.getElementById('loading-screen');
  if (loadingScreen) {
    loadingScreen.classList.add('fade-out');
    setTimeout(() => {
      loadingScreen.remove();
    }, 500);
  }
  
  // Render the app
  root.render(<AppWrapper />);
}

// Start the application
initializeApp();

// Hot module replacement (development only)
if (import.meta.hot) {
  import.meta.hot.accept('./App', () => {
    initializeApp();
  });
}
